import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Pause, Play, Trash2, Archive, MapPin, Award } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/ConfirmModal';
import { adminApi } from '../../lib/api';

import { CATEGORY_LIST, getSubcategories } from '../../config/businessCategories';

// Phase 1 plans (ADR-0009 PD-4). `subscription_tiers` is the source of truth; this map
// only names the keys we can still meet on old rows when that fetch comes back empty.
// Every other key is a pre-relaunch tier and gets one grey "Legacy" badge — the
// Mongo-era basic/medium/high/max names are never shown again.
export const PHASE1_TIERS = {
  free: { label: 'Free', color: '#9CA3AF' },
  local: { label: 'Local', color: '#FF7A1A' },
  multi: { label: 'Multi', color: '#6F5BEB' },
};
export const LEGACY_TIER = { label: 'Legacy', color: '#9CA3AF' };

const BusinessManagement = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [total, setTotal] = useState(0);
  const [archiveModal, setArchiveModal] = useState({ isOpen: false, businessId: null, businessName: '' });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, businessId: null, businessName: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hubLocations, setHubLocations] = useState([]);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);

  // Fetch hub locations and subscription tiers on mount
  useEffect(() => {
    fetchHubLocations();
    fetchSubscriptionTiers();
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [search, filterTier, filterLocation, filterStatus]);

  const fetchHubLocations = async () => {
    try {
      const response = await adminApi.get(`/admin/locations`);
      setHubLocations(response.data.locations || []);
    } catch (err) {
      console.error('Error fetching hub locations:', err);
    }
  };

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await adminApi.get(`/subscription-tiers/all`);
      setSubscriptionTiers(response.data.tiers || []);
    } catch (err) {
      console.error('Error fetching subscription tiers:', err);
    }
  };

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (search) params.append('search', search);
      if (filterTier) params.append('plan_tier', filterTier);
      if (filterLocation) params.append('location', filterLocation);
      if (filterStatus !== '') params.append('is_active', filterStatus);

      const response = await adminApi.get(`/admin/businesses?${params.toString()}`);
      
      setBusinesses(response.data.businesses);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching businesses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (businessId, currentStatus) => {
    try {
      await adminApi.patch(
        `/admin/businesses/${businessId}/status?is_active=${!currentStatus}`,
        {}
      );
      fetchBusinesses();
    } catch (err) {
      console.error('Error toggling business status:', err);
    }
  };

  const openArchiveModal = (business) => {
    setArchiveModal({
      isOpen: true,
      businessId: business.id,
      businessName: business.name
    });
    setErrorMessage('');
  };

  const closeArchiveModal = () => {
    setArchiveModal({ isOpen: false, businessId: null, businessName: '' });
    setErrorMessage('');
  };

  const handleArchive = async () => {
    setActionLoading(true);
    setErrorMessage('');
    
    try {
      await adminApi.patch(
        `/admin/businesses/${archiveModal.businessId}/archive?archived=true`,
        {}
      );
      closeArchiveModal();
      fetchBusinesses();
    } catch (err) {
      console.error('Error archiving business:', err);
      const d = err.response?.data?.detail;
      setErrorMessage(typeof d === 'string' ? d : d?.message || 'Failed to archive business. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (business) => {
    setDeleteModal({
      isOpen: true,
      businessId: business.id,
      businessName: business.name
    });
    setErrorMessage('');
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, businessId: null, businessName: '' });
    setErrorMessage('');
  };

  const handleDelete = async () => {
    setActionLoading(true);
    setErrorMessage('');
    
    try {
      await adminApi.delete(`/admin/businesses/${deleteModal.businessId}`);
      closeDeleteModal();
      fetchBusinesses();
    } catch (err) {
      console.error('Error deleting business:', err);
      
      // Handle structured error responses from backend
      let errorMsg = 'Failed to delete business. Please try again.';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // If detail is an object with a message property (structured error)
        if (typeof detail === 'object' && detail.message) {
          errorMsg = detail.message;
          
          // Show archive suggestion if available
          if (detail.can_archive) {
            errorMsg += ' Please use the Archive button instead.';
          }
        }
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const getTierColor = (tierKey) => {
    const tier = subscriptionTiers.find(t => t.tier_key === tierKey);
    if (tier) {
      const colorMap = {
        gray: '#9CA3AF',
        orange: '#FF7A1A',
        purple: '#6F5BEB',
        indigo: '#312E81',
        teal: '#37B3A8',
        blue: '#3B82F6'
      };
      return colorMap[tier.color_theme] || '#9CA3AF';
    }
    return PHASE1_TIERS[tierKey]?.color || LEGACY_TIER.color;
  };

  const getTierLabel = (tierKey) => {
    const tier = subscriptionTiers.find(t => t.tier_key === tierKey);
    if (tier) return tier.name;
    return PHASE1_TIERS[tierKey]?.label || LEGACY_TIER.label;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* V5 Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-business text-sm mb-1">// admin.businesses</p>
            <h1 className="font-display text-3xl text-navy">Businesses</h1>
            <p className="text-navy/50 mt-1">{total} total businesses</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => navigate('/admin/businesses/archived')}
              className="px-4 py-2.5 border border-navy/20 rounded-xl text-navy/70 hover:bg-white hover:border-navy/30 transition-all flex items-center gap-2"
            >
              <Archive size={18} />
              View Archived
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-business to-purple-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-business/20 transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Add Business
            </button>
          </div>
        </div>

        {/* V5 Search & Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-navy/30" />
              <input
                type="text"
                placeholder="Search businesses..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-business transition-colors"
              />
            </div>

            {/* Tier Filter */}
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy focus:outline-none focus:border-business transition-colors"
            >
              <option value="">All plans</option>
              {subscriptionTiers.length > 0 ? (
                subscriptionTiers.map(tier => (
                  <option key={tier.tier_key} value={tier.tier_key}>
                    {tier.name}
                  </option>
                ))
              ) : (
                Object.entries(PHASE1_TIERS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))
              )}
            </select>

            {/* Hub Location Filter */}
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy focus:outline-none focus:border-business transition-colors"
            >
              <option value="">All Hub Locations</option>
              {hubLocations.map(hub => (
                <option key={hub.id} value={hub.id}>
                  {hub.name} - {hub.city}, {hub.state}
                </option>
              ))}
            </select>

            {/* Search Button */}
            <button
              onClick={fetchBusinesses}
              className="px-6 py-3 bg-gradient-to-r from-business to-purple-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-business/20 transition-all"
            >
              Search
            </button>
          </div>
        </div>

        {/* Businesses Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-business border-t-transparent"></div>
          </div>
        ) : businesses.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 py-20 text-center">
            <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <Plus className="w-10 h-10 text-navy/20" />
            </div>
            <h3 className="font-display text-xl text-navy mb-2">No businesses found</h3>
            <p className="text-navy/50">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px]">
                <thead className="bg-cream/50 border-b border-navy/5">
                  <tr>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Business
                      </th>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Deals
                      </th>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Tier
                      </th>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Hub Locations
                      </th>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Location
                      </th>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Stats
                      </th>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Status
                      </th>
                      <th className="text-left p-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.map((business) => (
                      <tr key={business.id} className="border-t border-gray-200">
                        <td className="p-4">
                          <div>
                            <p className="font-semibold" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                              {business.name}
                            </p>
                            <p className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#9CA3AF' }}>
                              {business.slug}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div>
                            {business.deal_count > 0 ? (
                              <div>
                                <p className="text-sm font-semibold" style={{ fontFamily: 'Inter, sans-serif', color: '#6F5BEB' }}>
                                  {business.deal_count} {business.deal_count === 1 ? 'Deal' : 'Deals'}
                                </p>
                                <p className="text-xs" style={{ fontFamily: 'Inter, sans-serif', color: '#9CA3AF' }}>
                                  Active deals available
                                </p>
                              </div>
                            ) : (
                              <p className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#EF4444' }}>
                                No deals
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Award size={16} style={{ color: getTierColor(business.plan_tier) }} />
                            <span
                              className="px-3 py-1 rounded-full text-sm font-semibold"
                              style={{
                                backgroundColor: `${getTierColor(business.plan_tier)}20`,
                                color: getTierColor(business.plan_tier),
                                fontFamily: 'Inter, sans-serif'
                              }}
                            >
                              {getTierLabel(business.plan_tier)}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          {business.hub_names && business.hub_names.length > 0 ? (
                            <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#475569' }}>
                              <span className="font-semibold">{business.hub_names.join(', ')}</span>
                              {business.hub_ids && business.hub_ids.length > 1 && (
                                <span className="text-xs block" style={{ color: '#9CA3AF' }}>
                                  {business.hub_ids.length} locations
                                </span>
                              )}
                            </div>
                          ) : business.hub_name ? (
                            <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#475569' }}>
                              <span className="font-semibold">{business.hub_name}</span>
                            </div>
                          ) : (
                            <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#EF4444' }}>
                              No hub
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          {(business.city || business.state || business.street_address || business.address || business.business_location) ? (
                            <div className="flex items-start gap-2">
                              <MapPin size={16} style={{ color: '#9CA3AF' }} className="mt-0.5" />
                              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#475569' }}>
                                {/* Handle address object (new schema) or string (old schema) */}
                                {business.address?.street && (
                                  <div>{business.address.street}</div>
                                )}
                                {!business.address?.street && business.street_address && typeof business.street_address === 'string' && (
                                  <div>{business.street_address}</div>
                                )}
                                {!business.address?.street && typeof business.street_address === 'object' && business.street_address?.street && (
                                  <div>{business.street_address.street}</div>
                                )}
                                {/* City, State, Zip - handle both new address object and old fields */}
                                {(business.address?.city || business.city || business.address?.state || business.state || business.address?.zip_code || business.zip_code) ? (
                                  <div>
                                    {(business.address?.city || business.city) && <span>{business.address?.city || business.city}</span>}
                                    {(business.address?.city || business.city) && (business.address?.state || business.state) && <span>, </span>}
                                    {(business.address?.state || business.state) && <span>{business.address?.state || business.state}</span>}
                                    {(business.address?.zip_code || business.zip_code) && <span> {business.address?.zip_code || business.zip_code}</span>}
                                  </div>
                                ) : business.business_location ? (
                                  <div>{business.business_location}</div>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#9CA3AF' }}>
                              No location
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#475569' }}>
                            <div>{business.total_views || 0} views</div>
                            <div>{business.total_claims || 0} claims</div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span
                            className="px-3 py-1 rounded-full text-sm font-semibold"
                            style={{
                              backgroundColor: business.is_active ? '#10B98120' : '#EF444420',
                              color: business.is_active ? '#10B981' : '#EF4444',
                              fontFamily: 'Inter, sans-serif'
                            }}
                          >
                            {business.is_active ? 'Active' : 'Paused'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/admin/businesses/${business.id}/edit`)}
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="Edit"
                            >
                              <Edit2 size={18} style={{ color: '#6F5BEB' }} />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(business.id, business.is_active)}
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title={business.is_active ? 'Pause' : 'Activate'}
                            >
                              {business.is_active ? (
                                <Pause size={18} style={{ color: '#FF7A1A' }} />
                              ) : (
                                <Play size={18} style={{ color: '#10B981' }} />
                              )}
                            </button>
                            <button
                              onClick={() => openArchiveModal(business)}
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="Archive"
                            >
                              <Archive size={18} style={{ color: '#F59E0B' }} />
                            </button>
                            <button
                              onClick={() => openDeleteModal(business)}
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="Delete Permanently"
                            >
                              <Trash2 size={18} style={{ color: '#EF4444' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingBusiness) && (
        <BusinessModal
          business={editingBusiness}
          subscriptionTiers={subscriptionTiers}
          onClose={() => {
            setShowAddModal(false);
            setEditingBusiness(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setEditingBusiness(null);
            fetchBusinesses();
          }}
        />
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={archiveModal.isOpen}
        onClose={closeArchiveModal}
        onConfirm={handleArchive}
        title="Archive Business"
        message={
          <div>
            <p className="mb-2">Archive <strong>{archiveModal.businessName}</strong>?</p>
            <p className="text-sm" style={{ color: '#6B7280' }}>You can restore it later from the Archived Businesses page.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Archive"
        cancelText="Cancel"
        danger={false}
        loading={actionLoading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Business"
        message={
          <div>
            <p className="mb-2">Are you sure you want to delete <strong>{deleteModal.businessName}</strong>?</p>
            <p className="text-sm text-red-600">This action cannot be undone. All associated offers and claims will be permanently deleted.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Delete Business"
        cancelText="Cancel"
        danger={true}
        loading={actionLoading}
      />
    </AdminLayout>
  );
};

// Business Add/Edit Modal Component
const BusinessModal = ({ business, subscriptionTiers = [], onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: business?.name || '',
    slug: business?.slug || '',
    description: business?.description || '',
    website: business?.website || '',
    category: business?.category || '',
    subcategory: business?.subcategory || '',
    plan_tier: business?.plan_tier || 'free',
    hub_id: business?.hub_id || '',
    street_address: business?.street_address || '',
    city: business?.city || '',
    state: business?.state || '',
    zip_code: business?.zip_code || '',
    phone: business?.phone || '',
    contact_first_name: business?.contact_first_name || '',
    contact_last_name: business?.contact_last_name || '',
    contact_email: business?.contact_email || '',
    owner_id: business?.owner_id || '',
    owner_email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(business?.image_url || null);
  
  // Auto-generate slug from business name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-'); // Replace multiple hyphens with single
  };
  
  // Format website URL (add https:// if needed)
  const formatWebsiteUrl = (url) => {
    if (!url) return '';
    let formatted = url.trim().toLowerCase();
    // Remove any existing protocol
    formatted = formatted.replace(/^(https?:\/\/)?(www\.)?/i, '');
    // Add https://www. prefix
    if (formatted) {
      formatted = `https://www.${formatted}`;
    }
    return formatted;
  };
  const [uploadingImage, setUploadingImage] = useState(false);
  const [hubLocations, setHubLocations] = useState([]);
  
  // Owner selection state
  const [ownerMode, setOwnerMode] = useState('existing'); // 'existing' or 'new'
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchingUsers, setSearchingUsers] = useState(false);

  // Fetch hub locations when modal opens
  useEffect(() => {
    const fetchHubLocations = async () => {
      try {
        const response = await adminApi.get(`/admin/locations`);
        setHubLocations(response.data.locations);
      } catch (err) {
        console.error('Error fetching hub locations:', err);
      }
    };
    fetchHubLocations();
  }, []);

  // Debounced user search
  useEffect(() => {
    if (ownerMode !== 'existing' || userSearchQuery.length < 2) {
      setUserSearchResults([]);
      return;
    }
    
    const searchTimeout = setTimeout(async () => {
      try {
        setSearchingUsers(true);
        const response = await adminApi.get(`/admin/users/search?q=${encodeURIComponent(userSearchQuery)}`);
        setUserSearchResults(response.data.users || []);
      } catch (err) {
        console.error('Error searching users:', err);
      } finally {
        setSearchingUsers(false);
      }
    }, 300);
    
    return () => clearTimeout(searchTimeout);
  }, [userSearchQuery, ownerMode]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, or WebP');
      return;
    }

    // Validate file size (8MB)
    const maxSize = 8 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is 8MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    setImageFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', imageFile);

      const response = await adminApi.post(`/admin/businesses/upload-image`, uploadFormData, { headers: { 'Content-Type': 'multipart/form-data' } });

      return response.data.image_url;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Upload image if new file selected
      let imageUrl = formData.image_url;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      // Clean up data before submitting - remove empty strings, set to null or undefined
      const dataToSubmit = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        // Convert empty strings to null for optional fields, except name
        if (value === '' && key !== 'name') {
          acc[key] = null;
        } else if (key === 'hub_id' && value) {
          // Convert hub_id to hub_ids array for API
          acc['hub_ids'] = [value];
        } else if (key === 'website' && value) {
          // Format website URL with https://
          acc[key] = formatWebsiteUrl(value);
        } else if (value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      // Auto-generate slug from business name if creating new business
      if (!business) {
        dataToSubmit.slug = generateSlug(formData.name);
      }
      
      // Add image URL
      dataToSubmit.image_url = imageUrl;
      
      // Handle owner assignment
      if (ownerMode === 'existing' && selectedUser) {
        dataToSubmit.owner_id = selectedUser.id;
        dataToSubmit.owner_email = null;
      } else if (ownerMode === 'new' && formData.owner_email) {
        dataToSubmit.owner_id = null;
        dataToSubmit.owner_email = formData.owner_email;
      } else {
        dataToSubmit.owner_id = null;
        dataToSubmit.owner_email = null;
      }

      if (business) {
        // Update existing business
        await adminApi.put(`/admin/businesses/${business.id}`, dataToSubmit);
      } else {
        // Create new business
        await adminApi.post(`/admin/businesses`, dataToSubmit);
      }

      onSuccess();
    } catch (err) {
      console.error('Update error:', err.response?.data);
      // Handle Pydantic validation errors
      if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
        const errors = err.response.data.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        setError(errors);
      } else {
        setError(err.response?.data?.detail || err.message || 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl border-0 shadow-2xl my-8" style={{ borderRadius: '24px' }}>
        <CardContent className="p-8 max-h-[80vh] overflow-y-auto">
          <h2 className="font-display text-3xl font-bold mb-6" style={{ color: '#111827' }}>
            {business ? 'Edit Business' : 'Add New Business'}
          </h2>

          {error && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#FEE2E2' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', color: '#DC2626' }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Business Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
                placeholder="Enter business name"
              />
              {!business && formData.name && (
                <p className="text-xs text-gray-500 mt-1">
                  URL slug will be: <span className="font-mono text-gray-700">{generateSlug(formData.name)}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Website
              </label>
              <Input
                type="text"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="rounded-xl"
                placeholder="www.example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter as www.example.com — https:// will be added automatically
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Business Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="3"
                className="w-full px-4 py-2 rounded-xl border border-gray-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder="Describe what makes this business special..."
              />
            </div>

            {/* Category and Subcategory */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                  Main Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    category: e.target.value,
                    subcategory: '' // Reset subcategory when category changes
                  })}
                  className="w-full px-4 py-2 rounded-xl border border-gray-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">Select Category</option>
                  {CATEGORY_LIST.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                  Subcategory
                </label>
                <select
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-gray-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  disabled={!formData.category}
                >
                  <option value="">Select Subcategory</option>
                  {formData.category && getSubcategories(formData.category).map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Plan Tier *
              </label>
              <select
                value={formData.plan_tier}
                onChange={(e) => setFormData({ ...formData, plan_tier: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-xl border border-gray-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {subscriptionTiers.length > 0 ? (
                  subscriptionTiers.map(tier => (
                    <option key={tier.tier_key} value={tier.tier_key}>
                      {tier.name} — ${tier.monthly_price}/mo
                      {tier.hub_cap ? ` · ${tier.hub_cap} venue${tier.hub_cap !== 1 ? 's' : ''}` : ''}
                      {tier.deal_limit ? ` · ${tier.deal_limit} deal${tier.deal_limit !== 1 ? 's' : ''}` : ''}
                      {tier.admin_only ? ' [legacy / admin only]' : ''}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="free">Free — $0/mo (admin comp)</option>
                    <option value="local">Local — $125/mo · 1 venue · 1 deal</option>
                    <option value="multi">Multi — $199/mo · 3 venues · 3 deals</option>
                  </>
                )}
              </select>
            </div>

            {/* Business Owner Assignment */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Business Owner <span className="text-sm font-normal text-gray-500">(Optional)</span>
              </h3>
              <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                This is optional. You can invite a user to manage this business later from the business edit page once you have their email.
              </p>
              
              {/* Owner Mode Toggle */}
              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ownerMode"
                    checked={ownerMode === 'existing'}
                    onChange={() => {
                      setOwnerMode('existing');
                      setFormData({ ...formData, owner_email: '' });
                    }}
                    className="w-4 h-4 text-orange-500"
                  />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151' }}>Existing User</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ownerMode"
                    checked={ownerMode === 'new'}
                    onChange={() => {
                      setOwnerMode('new');
                      setSelectedUser(null);
                      setUserSearchQuery('');
                    }}
                    className="w-4 h-4 text-orange-500"
                  />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151' }}>Invite New User</span>
                </label>
              </div>
              
              {ownerMode === 'existing' ? (
                <div className="relative">
                  <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                    Search Users
                  </label>
                  {selectedUser ? (
                    <div className="flex items-center justify-between p-3 rounded-xl border border-green-300 bg-green-50">
                      <div>
                        <p className="font-semibold" style={{ color: '#111827' }}>{selectedUser.display_name}</p>
                        <p className="text-sm text-gray-500">{selectedUser.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearchQuery('');
                        }}
                        className="text-red-500 hover:text-red-700 text-sm font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        placeholder="Search by name or email..."
                        className="rounded-xl"
                      />
                      {searchingUsers && (
                        <p className="text-sm mt-2 text-gray-500">Searching...</p>
                      )}
                      {userSearchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                          {userSearchResults.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                setSelectedUser(user);
                                setUserSearchQuery('');
                                setUserSearchResults([]);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-100 last:border-b-0"
                            >
                              <p className="font-semibold text-gray-900">{user.display_name}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <p className="text-xs text-gray-500 mt-2">Leave empty to create without an owner</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                    New User Email
                  </label>
                  <Input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                    placeholder="Enter email address..."
                    className="rounded-xl"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    An invitation email will be sent to verify and accept ownership
                  </p>
                </div>
              )}
              
              {/* Contact Information - under Business Owner section */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Inter, sans-serif', color: '#374151' }}>
                  Contact Information
                </h4>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                      First Name
                    </label>
                    <Input
                      type="text"
                      value={formData.contact_first_name}
                      onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })}
                      className="rounded-xl"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                      Last Name
                    </label>
                    <Input
                      type="text"
                      value={formData.contact_last_name}
                      onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })}
                      className="rounded-xl"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="rounded-xl"
                    placeholder="contact@business.com"
                  />
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Business Image (Max: 1920x1080, 8MB)
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleImageChange}
                className="w-full px-4 py-2 rounded-xl border border-gray-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
              {imagePreview && (
                <div className="mt-3">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              )}
              {uploadingImage && (
                <p className="text-sm mt-2" style={{ color: '#6F5BEB' }}>Uploading image...</p>
              )}
            </div>

            {/* Business Address Section */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Business Address
              </h3>
              
              {/* Street Address */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                  Street Address
                </label>
                <Input
                  type="text"
                  value={formData.street_address}
                  onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                  className="rounded-xl"
                  placeholder="123 Main Street"
                />
              </div>
              
              {/* City, State, ZIP in a row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                    City
                  </label>
                  <Input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="rounded-xl"
                    placeholder="Medford"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                    State
                  </label>
                  <Input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="rounded-xl"
                    placeholder="NJ"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                    ZIP Code
                  </label>
                  <Input
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="rounded-xl"
                    placeholder="08055"
                  />
                </div>
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Phone Number
              </label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="rounded-xl"
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Hub Location (Restaurant/Bar) */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Hub Location * (Restaurant/Bar using Tappd)
              </label>
              <select
                value={formData.hub_id}
                onChange={(e) => setFormData({ ...formData, hub_id: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-xl border border-gray-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="">Select hub location...</option>
                {hubLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name} - {loc.city}, {loc.state}
                  </option>
                ))}
              </select>
              {hubLocations.length === 0 && (
                <p className="text-sm mt-1" style={{ color: '#EF4444' }}>
                  No hub locations available. Create one in Locations tab first.
                </p>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={onClose}
                className="flex-1"
                style={{ backgroundColor: '#E5E7EB', color: '#111827' }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
                style={{ backgroundColor: '#6F5BEB', color: '#FFFFFF' }}
              >
                {loading ? 'Saving...' : (business ? 'Update Business' : 'Create Business')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessManagement;
